import { useState } from 'react';
import { useHayCambios } from '../hooks/useHayCambios';
import { useCierreConfirmado } from '../hooks/useCierreConfirmado';
import { useApi } from '../hooks/useApi';
import { DataTable } from '../components/DataTable';
import { Modal } from '../components/Modal';
import { useToast } from '../components/Toast';
import { HelpTip } from '../components/HelpTip';
import { api, extractErrorMessage, getAccessToken } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { Button } from '../components/Button';

interface JurisdiccionFiscal {
  id: number;
  nombre: string;
  codigo: string;
  activo: boolean;
}

interface ConfiguracionImpositiva {
  id: number;
  jurisdiccionId: number;
  tipo: string;
  alicuotaRetencion: number;
  alicuotaPercepcion: number;
  montoMinimoNoImponible: number;
  montoMinimoRetencion: number;
  alicuotaDefault: number;
  vigenciaDesde: string;
  vigenciaHasta: string | null;
}

interface ImportResult {
  creados: number;
  actualizados: number;
  fallos: number;
  errores: Array<{ fila: number; campo?: string; mensaje: string; valor?: string }>;
}

const emptyJurisdiccionForm = () => ({ nombre: '', codigo: '', activo: true });

const emptyConfigForm = () => ({
  jurisdiccionId: 0,
  tipo: 'iibb',
  alicuotaRetencion: 0,
  alicuotaPercepcion: 0,
  montoMinimoNoImponible: 0,
  montoMinimoRetencion: 0,
  alicuotaDefault: 0,
  vigenciaDesde: new Date().toISOString().slice(0, 10),
  vigenciaHasta: '',
});

export function ConfiguracionImpositivaPage() {
  const toast = useToast();
  const { can } = useAuth();

  // ── Jurisdicciones ──────────────────────────────────────────────────────
  const { data: jurisdicciones, loading: loadingJurisdicciones, refetch: refetchJurisdicciones } =
    useApi<JurisdiccionFiscal[]>('/api/v3/impositivo/jurisdicciones');

  const [jurModalOpen, setJurModalOpen] = useState(false);
  const [jurForm, setJurForm] = useState(emptyJurisdiccionForm());
  const [jurSubmitting, setJurSubmitting] = useState(false);
  const cierreJur = useCierreConfirmado(useHayCambios(jurForm, jurModalOpen), () => setJurModalOpen(false));

  const handleCreateJurisdiccion = async () => {
    if (!jurForm.nombre.trim() || !jurForm.codigo.trim()) {
      toast.addToast('Nombre y código son obligatorios', 'error');
      return;
    }
    setJurSubmitting(true);
    try {
      await api.post('/api/v3/impositivo/jurisdicciones', jurForm);
      toast.addToast('Jurisdicción creada', 'success');
      setJurModalOpen(false);
      setJurForm(emptyJurisdiccionForm());
      refetchJurisdicciones();
    } catch (e) { toast.addToast(extractErrorMessage(e), 'error'); }
    finally { setJurSubmitting(false); }
  };

  const jurisdiccionColumns = [
    { key: 'nombre', header: 'Nombre' },
    { key: 'codigo', header: 'Código' },
    {
      key: 'activo',
      header: 'Activo',
      render: (r: JurisdiccionFiscal) => (
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${r.activo ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
          {r.activo ? 'Sí' : 'No'}
        </span>
      ),
      filterable: false,
    },
  ];

  // ── Configuraciones (alícuotas) ─────────────────────────────────────────
  const { data: configuraciones, loading: loadingConfig, refetch: refetchConfig } =
    useApi<ConfiguracionImpositiva[]>('/api/v3/impositivo/configuraciones');

  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [configEditId, setConfigEditId] = useState<number | null>(null);
  const [configForm, setConfigForm] = useState(emptyConfigForm());
  const [configSubmitting, setConfigSubmitting] = useState(false);
  const cierreConfig = useCierreConfirmado(useHayCambios(configForm, configModalOpen), () => setConfigModalOpen(false));

  const jurisdiccionNombre = (id: number) =>
    jurisdicciones?.find(j => j.id === id)?.nombre ?? `#${id}`;

  const handleEditConfig = (row: ConfiguracionImpositiva) => {
    if (!can('impositivo', 'editar')) { toast.addToast('No tenés permiso para realizar esta acción', 'error'); return; }
    setConfigEditId(row.id);
    setConfigForm({
      jurisdiccionId: row.jurisdiccionId,
      tipo: row.tipo,
      alicuotaRetencion: Number(row.alicuotaRetencion),
      alicuotaPercepcion: Number(row.alicuotaPercepcion),
      montoMinimoNoImponible: Number(row.montoMinimoNoImponible),
      montoMinimoRetencion: Number(row.montoMinimoRetencion),
      alicuotaDefault: Number(row.alicuotaDefault),
      vigenciaDesde: row.vigenciaDesde.slice(0, 10),
      vigenciaHasta: row.vigenciaHasta ? row.vigenciaHasta.slice(0, 10) : '',
    });
    setConfigModalOpen(true);
  };

  const handleNewConfig = () => {
    setConfigEditId(null);
    setConfigForm(emptyConfigForm());
    setConfigModalOpen(true);
  };

  const handleSaveConfig = async () => {
    setConfigSubmitting(true);
    try {
      const body: any = {
        ...configForm,
        vigenciaHasta: configForm.vigenciaHasta || undefined,
      };
      if (configEditId) {
        await api.patch(`/api/v3/impositivo/configuraciones/${configEditId}`, body);
        toast.addToast('Configuración actualizada', 'success');
      } else {
        await api.post('/api/v3/impositivo/configuraciones', body);
        toast.addToast('Configuración creada', 'success');
      }
      setConfigModalOpen(false);
      refetchConfig();
    } catch (e) { toast.addToast(extractErrorMessage(e), 'error'); }
    finally { setConfigSubmitting(false); }
  };

  const configColumns = [
    { key: 'jurisdiccionId', header: 'Jurisdicción', render: (r: ConfiguracionImpositiva) => jurisdiccionNombre(r.jurisdiccionId) },
    { key: 'tipo', header: 'Tipo' },
    { key: 'alicuotaRetencion', header: 'Al. Retención %', render: (r: ConfiguracionImpositiva) => Number(r.alicuotaRetencion).toFixed(3) },
    { key: 'alicuotaPercepcion', header: 'Al. Percepción %', render: (r: ConfiguracionImpositiva) => Number(r.alicuotaPercepcion).toFixed(3) },
    { key: 'montoMinimoNoImponible', header: 'Mín. No Imponible', render: (r: ConfiguracionImpositiva) => `$${Number(r.montoMinimoNoImponible).toFixed(2)}` },
    { key: 'alicuotaDefault', header: 'Al. Default', render: (r: ConfiguracionImpositiva) => Number(r.alicuotaDefault).toFixed(3) },
    { key: 'vigenciaDesde', header: 'Vigente desde', render: (r: ConfiguracionImpositiva) => r.vigenciaDesde.slice(0, 10) },
  ];

  // ── Padrón upload ────────────────────────────────────────────────────────
  const [padronJurisdiccionId, setPadronJurisdiccionId] = useState<number>(0);
  const [padronPeriodo, setPadronPeriodo] = useState('');
  const [padronDelimiter, setPadronDelimiter] = useState('');
  const [padronLayoutJson, setPadronLayoutJson] = useState('');
  const [padronDryRun, setPadronDryRun] = useState(false);
  const [padronUploading, setPadronUploading] = useState(false);
  const [padronResult, setPadronResult] = useState<ImportResult | null>(null);

  const handleImportPadron = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!padronJurisdiccionId || !padronPeriodo.trim()) {
      toast.addToast('Seleccioná jurisdicción y período (YYYY-MM)', 'error');
      e.target.value = '';
      return;
    }
    setPadronUploading(true);
    setPadronResult(null);
    try {
      const params = new URLSearchParams({
        jurisdiccionId: String(padronJurisdiccionId),
        periodo: padronPeriodo.trim(),
        dryRun: String(padronDryRun),
      });
      if (padronDelimiter.trim()) params.set('delimiter', padronDelimiter.trim());

      const formData = new FormData();
      formData.append('archivo', file);
      if (padronLayoutJson.trim()) formData.append('layout', padronLayoutJson.trim());

      const token = getAccessToken();
      const headers: Record<string, string> = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const res = await fetch(`/api/v3/impositivo/padron/importar?${params.toString()}`, {
        method: 'POST',
        body: formData,
        headers,
        credentials: 'include',
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(body?.detail || body?.message || 'Error al importar padrón');
      }
      setPadronResult(body as ImportResult);
      toast.addToast(
        padronDryRun ? 'Validación (dry-run) completada' : 'Padrón importado correctamente',
        'success',
      );
    } catch (err) {
      toast.addToast(extractErrorMessage(err), 'error');
    } finally {
      setPadronUploading(false);
      e.target.value = '';
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-800 inline">Configuración Impositiva</h1>
        <HelpTip
          tips={[
            'Jurisdicciones fiscales y su configuración de retenciones/percepciones IIBB.',
            'Alícuotas, mínimos y default son editables — un cambio de tasa es configuración, no código.',
            'El padrón permite resolver la condición fiscal de un proveedor por CUIT.',
          ]}
        />
        <p className="text-slate-500 text-sm mt-1">Jurisdicciones, alícuotas y padrón de contribuyentes</p>
      </div>

      {/* ── Jurisdicciones ── */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-800">Jurisdicciones</h2>
          <Button
            onClick={() => { setJurForm(emptyJurisdiccionForm()); setJurModalOpen(true); }}
            disabled={!can('impositivo', 'crear')}
            title={!can('impositivo', 'crear') ? 'No tenés permiso para realizar esta acción' : undefined}
          >
            + Nueva Jurisdicción
          </Button>
        </div>
        <DataTable
          columns={jurisdiccionColumns}
          data={jurisdicciones ?? []}
          loading={loadingJurisdicciones}
          title="Jurisdicciones"
        />
      </section>

      {/* ── Configuraciones (alícuotas) ── */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-800">Alícuotas y Mínimos</h2>
          <Button
            onClick={handleNewConfig}
            disabled={!can('impositivo', 'crear')}
            title={!can('impositivo', 'crear') ? 'No tenés permiso para realizar esta acción' : undefined}
          >
            + Nueva Configuración
          </Button>
        </div>
        <DataTable
          columns={configColumns}
          data={configuraciones ?? []}
          loading={loadingConfig}
          onRowClick={handleEditConfig}
          title="ConfiguracionImpositiva"
        />
      </section>

      {/* ── Padrón upload ── */}
      <section className="space-y-3">
        <h2 className="text-lg font-semibold text-slate-800">Importar Padrón</h2>
        <div className="bg-white rounded-lg border border-slate-200 p-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Jurisdicción</label>
              <select
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                value={padronJurisdiccionId || ''}
                onChange={e => setPadronJurisdiccionId(Number(e.target.value))}
              >
                <option value="">-- Seleccionar --</option>
                {(jurisdicciones ?? []).map(j => (
                  <option key={j.id} value={j.id}>{j.nombre}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Período (YYYY-MM)</label>
              <input
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                placeholder="2026-07"
                value={padronPeriodo}
                onChange={e => setPadronPeriodo(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Delimitador (opcional)</label>
              <input
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                placeholder="Ej: ; (vacío = CSV estándar)"
                value={padronDelimiter}
                onChange={e => setPadronDelimiter(e.target.value)}
              />
            </div>
            <div className="flex items-end pb-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={padronDryRun}
                  onChange={e => setPadronDryRun(e.target.checked)}
                  className="rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                />
                <span className="text-sm font-medium">Dry-run (no persistir)</span>
              </label>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Layout fixed-width (JSON avanzado, opcional)
            </label>
            <textarea
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-xs font-mono"
              rows={2}
              placeholder='[{"field":"cuit","start":0,"length":11},{"field":"condicion","start":11,"length":2},{"field":"alicuota","start":13,"length":6}]'
              value={padronLayoutJson}
              onChange={e => setPadronLayoutJson(e.target.value)}
            />
          </div>

          <label
            onClick={(e) => { if (!can('padron', 'crear')) e.preventDefault(); }}
            className={`inline-block px-4 py-2 rounded-lg text-sm font-medium ${
              !can('padron', 'crear') || padronUploading
                ? 'bg-slate-100 text-slate-400 cursor-not-allowed opacity-50'
                : 'bg-slate-100 hover:bg-slate-200 text-slate-700 cursor-pointer'
            }`}
            title={!can('padron', 'crear') ? 'No tenés permiso para realizar esta acción' : 'Subir archivo de padrón (CSV, XLSX o TXT fixed-width)'}
          >
            📄 {padronUploading ? 'Importando...' : 'Subir archivo'}
            <input
              type="file"
              accept=".csv,.xlsx,.txt"
              onChange={handleImportPadron}
              className="hidden"
              disabled={!can('padron', 'crear') || padronUploading}
            />
          </label>

          {padronResult && (
            <div className="text-sm border-t border-slate-100 pt-3 space-y-1">
              <p className="text-slate-700">
                Creados: <strong>{padronResult.creados}</strong> · Fallos: <strong>{padronResult.fallos}</strong>
              </p>
              {padronResult.errores.length > 0 && (
                <ul className="text-xs text-red-600 list-disc list-inside">
                  {padronResult.errores.slice(0, 10).map((err, i) => (
                    <li key={i}>Fila {err.fila}: {err.mensaje}{err.campo ? ` (${err.campo})` : ''}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </section>

      {/* ── Modal: Nueva Jurisdicción ── */}
      <Modal
        open={jurModalOpen}
        cierre={cierreJur}
        title="Nueva Jurisdicción"
        size="sm"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Nombre</label>
            <input
              value={jurForm.nombre}
              onChange={e => setJurForm({ ...jurForm, nombre: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
              placeholder="Ej: Mendoza"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Código</label>
            <input
              value={jurForm.codigo}
              onChange={e => setJurForm({ ...jurForm, codigo: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
              placeholder="Ej: MDZ"
            />
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button onClick={cierreJur.cerrar} className="px-4 py-2 text-sm">Cancelar</button>
            <Button onClick={handleCreateJurisdiccion} loading={jurSubmitting}>
              {jurSubmitting ? 'Guardando...' : 'Crear'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* ── Modal: Configuración (crear/editar) ── */}
      <Modal
        open={configModalOpen}
        cierre={cierreConfig}
        title={configEditId ? `Editar Configuración #${configEditId}` : 'Nueva Configuración'}
        size="lg"
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Jurisdicción</label>
              <select
                value={configForm.jurisdiccionId || ''}
                onChange={e => setConfigForm({ ...configForm, jurisdiccionId: Number(e.target.value) })}
                className="w-full px-3 py-2 border rounded-lg"
                disabled={!!configEditId}
              >
                <option value="">-- Seleccionar --</option>
                {(jurisdicciones ?? []).map(j => (
                  <option key={j.id} value={j.id}>{j.nombre}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Tipo</label>
              <input
                value={configForm.tipo}
                onChange={e => setConfigForm({ ...configForm, tipo: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="iibb, ganancias..."
                disabled={!!configEditId}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Alícuota Retención %</label>
              <input
                type="number" step="0.001"
                value={configForm.alicuotaRetencion}
                onChange={e => setConfigForm({ ...configForm, alicuotaRetencion: +e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Alícuota Percepción %</label>
              <input
                type="number" step="0.001"
                value={configForm.alicuotaPercepcion}
                onChange={e => setConfigForm({ ...configForm, alicuotaPercepcion: +e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Mínimo No Imponible</label>
              <input
                type="number" step="0.01"
                value={configForm.montoMinimoNoImponible}
                onChange={e => setConfigForm({ ...configForm, montoMinimoNoImponible: +e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Monto Mínimo de Retención</label>
              <input
                type="number" step="0.01"
                value={configForm.montoMinimoRetencion}
                onChange={e => setConfigForm({ ...configForm, montoMinimoRetencion: +e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Alícuota Default (CUIT no en padrón)</label>
              <input
                type="number" step="0.001"
                value={configForm.alicuotaDefault}
                onChange={e => setConfigForm({ ...configForm, alicuotaDefault: +e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Vigente desde</label>
              <input
                type="date"
                value={configForm.vigenciaDesde}
                onChange={e => setConfigForm({ ...configForm, vigenciaDesde: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Vigente hasta (opcional)</label>
              <input
                type="date"
                value={configForm.vigenciaHasta}
                onChange={e => setConfigForm({ ...configForm, vigenciaHasta: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button onClick={cierreConfig.cerrar} className="px-4 py-2 text-sm">Cancelar</button>
            <Button
              onClick={handleSaveConfig}
              loading={configSubmitting}
              disabled={configEditId ? !can('impositivo', 'editar') : !can('impositivo', 'crear')}
            >
              {configSubmitting ? 'Guardando...' : configEditId ? 'Guardar' : 'Crear'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
